import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "./components/AppLayout.js";
import { Forbidden } from "./components/Forbidden.js";
import { AdminUsersPage } from "./pages/AdminUsersPage.js";
import { AuditLogPage } from "./pages/AuditLogPage.js";
import { ChatPage } from "./pages/ChatPage.js";
import { CustomerDetailPage } from "./pages/CustomerDetailPage.js";
import { CustomersPage } from "./pages/CustomersPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { EventDetailPage } from "./pages/EventDetailPage.js";
import { EventsListPage } from "./pages/EventsListPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { OperatorsPage } from "./pages/OperatorsPage.js";
import { useAuth, useScope } from "./store/auth.js";

/** Redirects to /login when there is no session. */
function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { token } = useAuth();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <AppLayout>{children}</AppLayout>;
}

/** Portal-admin-only wrapper — customer admins see the 403 view. */
function RequirePortalAdmin({ children }: { children: ReactNode }): JSX.Element {
  const { isPortalAdmin } = useScope();
  if (!isPortalAdmin) {
    return <Forbidden message="The Customers area is available to portal admins only." />;
  }
  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/customers"
        element={
          <RequireAuth>
            <RequirePortalAdmin>
              <CustomersPage />
            </RequirePortalAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/customers/:customerId"
        element={
          <RequireAuth>
            <RequirePortalAdmin>
              <CustomerDetailPage />
            </RequirePortalAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/events"
        element={
          <RequireAuth>
            <EventsListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <RequireAuth>
            <EventDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/operators"
        element={
          <RequireAuth>
            <OperatorsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin-users"
        element={
          <RequireAuth>
            <AdminUsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/audit"
        element={
          <RequireAuth>
            <AuditLogPage />
          </RequireAuth>
        }
      />
      <Route
        path="/chat"
        element={
          <RequireAuth>
            <ChatPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
